import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Car,
  Check,
  ClipboardCheck,
  MapPinned,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { MobilePage } from "../MobilePage";
import type { MapPoint } from "./directoryMapData";
import {
  markLongLegs,
  orderByNearestNeighbor,
  recomputeLegs,
  type RoutePoint,
  type ValidPoint,
} from "./nearestNeighborRoute";
import { googleMapsRouteSegments, type TravelMode } from "./googleMapsRouteUrl";
import { NextRoundButton } from "./NextRoundButton";
import {
  discardDirectory,
  loadOpenRound,
  saveOpenRound,
  type RestoredRound,
} from "./roundPersistence";
import { RouteMap } from "./RouteMap";
import { VisitReport } from "./VisitReport";
import { filterOfferSlug } from "./visitReport";

type RouteLocationState =
  | {
      stops: MapPoint[];
      /** Modo A: ubicación de partida (solo en memoria, viaja por router state) */
      start?: RoutePoint;
      /** filtro del Directorio con que se armó la ronda (para "siguiente ronda") */
      filter?: Record<string, unknown>;
      size?: number;
      /** ids de rondas anteriores de la sesión (para "siguiente ronda") */
      seen?: number[];
      /** ronda recuperada de la base: respetar el orden guardado */
      keepOrder?: boolean;
    }
  | null
  | undefined;

const isValidPoint = (p: MapPoint): p is ValidPoint =>
  typeof p.lat === "number" &&
  typeof p.lng === "number" &&
  Number.isFinite(p.lat) &&
  Number.isFinite(p.lng) &&
  Math.abs(p.lat) <= 90 &&
  Math.abs(p.lng) <= 180;

const withoutLeg = <T extends { legKm: number }>(p: T): Omit<T, "legKm"> => {
  const { legKm: _legKm, ...rest } = p;
  return rest;
};

const swap = <T,>(arr: T[], i: number, j: number): T[] => {
  const next = [...arr];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

type StartMode = "business" | "location";

/**
 * Panel de ruta (Fase 2, paso 2 del plan): toma las paradas armadas desde el
 * Directorio (router state, nunca la URL ni localStorage), calcula el orden
 * por vecino más cercano desde el punto de partida elegido, y permite
 * reordenar a mano o quitar paradas. Las coordenadas de "Mi ubicación" viven
 * solo en memoria (useState) — nunca se guardan. La ronda (paradas, orden y
 * visitas) se guarda en la base y se recupera si se entra sin router state.
 */
export const RoutePage = () => {
  // key: una "siguiente ronda" reemplaza el router state y debe reiniciar el estado local
  const location = useLocation();
  const state = location.state as RouteLocationState;
  return state?.stops ? (
    <RouteView key={location.key} state={state} />
  ) : (
    <RestoredRoute key={location.key} />
  );
};

const RestoredRoute = () => {
  const [round, setRound] = useState<RestoredRound | null | undefined>();
  useEffect(() => {
    let alive = true;
    loadOpenRound().then(
      (r) => alive && setRound(r),
      () => alive && setRound(null),
    );
    return () => {
      alive = false;
    };
  }, []);
  if (round === undefined)
    return (
      <MobilePage title="Ruta">
        <p className="p-6 text-center text-muted-foreground">
          Buscando tu ronda abierta…
        </p>
      </MobilePage>
    );
  return (
    <RouteView
      state={
        round && {
          stops: round.stops,
          filter: round.origen.filter,
          size: round.origen.size,
          seen: round.origen.seen,
          keepOrder: true,
        }
      }
      initialVisited={round?.visited}
    />
  );
};

const RouteView = ({
  state,
  initialVisited = [],
}: {
  state: RouteLocationState;
  initialVisited?: number[];
}) => {
  const navigate = useNavigate();
  const incoming = state?.stops ?? [];
  const [points] = useState<ValidPoint[]>(() => incoming.filter(isValidPoint));
  const skipped = incoming.length - points.length;

  const initialStart = state?.start ?? null;
  const [order, setOrder] = useState<ValidPoint[]>(() =>
    points.length === 0
      ? []
      : state?.keepOrder
        ? points
        : orderByNearestNeighbor(initialStart ?? points[0], points).map(
            withoutLeg,
          ),
  );
  const [startMode, setStartMode] = useState<StartMode>(
    initialStart ? "location" : "business",
  );
  const [userLocation, setUserLocation] = useState<RoutePoint | null>(
    initialStart,
  );
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [travelMode, setTravelMode] = useState<TravelMode>("walking");
  const [visiting, setVisiting] = useState<ValidPoint | null>(null);
  const [visited, setVisited] = useState<ReadonlySet<number>>(
    () => new Set(initialVisited),
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState<number | null>(null);
  const [discardError, setDiscardError] = useState<string | null>(null);

  // Guarda la ronda en cada cambio, en fila (una escritura a la vez, sin carreras).
  const saveChain = useRef<Promise<void>>(Promise.resolve());
  const filter = state?.filter;
  const size = state?.size;
  const seen = state?.seen;
  useEffect(() => {
    if (points.length === 0) return;
    const ids = order.map((p) => p.id);
    saveChain.current = saveChain.current
      .then(() => saveOpenRound(ids, visited, { filter, size, seen }))
      .then(
        () => setSaveError(null),
        () =>
          setSaveError(
            "No se pudo guardar la ronda; sigue aquí mientras no cierres esta pantalla.",
          ),
      );
  }, [points.length, order, visited, filter, size, seen]);

  const startPoint: RoutePoint | null =
    startMode === "location" && userLocation
      ? userLocation
      : (points[0] ?? null);

  const stops =
    startPoint && order.length > 0
      ? markLongLegs(recomputeLegs(startPoint, order))
      : [];

  // Con "negocio 1" como partida, ese negocio es el origen: no se repite como parada.
  const mapsSegments = startPoint
    ? googleMapsRouteSegments(
        startPoint,
        stops.filter((s) => startMode !== "business" || s.id !== points[0]?.id),
        travelMode,
      )
    : [];

  const useBusinessStart = () => {
    setStartMode("business");
    setGeoError(null);
    if (points[0])
      setOrder(orderByNearestNeighbor(points[0], order).map(withoutLeg));
  };

  const useMyLocation = () => {
    setGeoError(null);
    if (!("geolocation" in navigator)) {
      setGeoError(
        "Tu navegador no soporta ubicación. Usa un negocio de la lista como punto de partida.",
      );
      return;
    }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coord = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserLocation(coord);
        setStartMode("location");
        setGeoBusy(false);
        setOrder(orderByNearestNeighbor(coord, order).map(withoutLeg));
      },
      (err) => {
        setGeoBusy(false);
        setGeoError(
          err.code === err.PERMISSION_DENIED
            ? "No diste permiso de ubicación. Usa un negocio de la lista como punto de partida."
            : "No se pudo obtener tu ubicación. Usa un negocio de la lista como punto de partida.",
        );
      },
    );
  };

  const moveUp = (i: number) => i > 0 && setOrder((o) => swap(o, i, i - 1));
  const moveDown = (i: number) =>
    i < order.length - 1 && setOrder((o) => swap(o, i, i + 1));
  const remove = (id: number) => setOrder((o) => o.filter((p) => p.id !== id));

  // Primer toque pide confirmar; el segundo descarta para siempre.
  const discard = async (id: number) => {
    setDiscardError(null);
    if (confirmDiscard !== id) {
      setConfirmDiscard(id);
      return;
    }
    setConfirmDiscard(null);
    try {
      await discardDirectory(id);
      remove(id);
    } catch (e) {
      setDiscardError((e as Error).message);
    }
  };

  return (
    <MobilePage title="Ruta">
      <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="size-10"
            aria-label="Volver al Directorio"
            onClick={() => navigate("/directory")}
          >
            <ArrowLeft />
          </Button>
          <h1 className="text-xl font-bold">Ruta de visita</h1>
        </div>

        {points.length === 0 ? (
          <p className="py-6 text-center text-muted-foreground">
            No hay paradas seleccionadas. Vuelve al Directorio y selecciona al
            menos una empresa para armar una ruta.
          </p>
        ) : (
          <>
            {skipped > 0 && (
              <p className="text-sm text-muted-foreground">
                {skipped} empresa{skipped > 1 ? "s" : ""} sin ubicación se
                excluyeron de la ruta.
              </p>
            )}

            <div className="flex flex-col gap-2">
              <p className="text-sm font-medium">Punto de partida</p>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant={startMode === "business" ? "default" : "outline"}
                  className="h-12 whitespace-normal leading-tight"
                  onClick={useBusinessStart}
                >
                  {points[0]?.nombre ?? "Negocio 1 de la lista"}
                </Button>
                <Button
                  variant={startMode === "location" ? "default" : "outline"}
                  className="h-12"
                  disabled={geoBusy}
                  onClick={useMyLocation}
                >
                  {geoBusy ? "Buscando…" : "Mi ubicación"}
                </Button>
              </div>
              {geoError && (
                <p role="alert" className="text-sm text-destructive">
                  {geoError}
                </p>
              )}
            </div>

            {stops.length === 0 ? (
              <p className="py-6 text-center text-muted-foreground">
                No quedan paradas. Vuelve al Directorio para agregar más.
              </p>
            ) : (
              <>
                {startPoint && (
                  <RouteMap
                    start={startPoint}
                    stops={stops.map((s) => ({
                      id: s.id,
                      lat: s.lat,
                      lng: s.lng,
                    }))}
                  />
                )}

                <ol className="flex flex-col gap-2">
                  {stops.map((s, i) => (
                    <li
                      key={s.id}
                      className="flex items-center gap-2 rounded-lg border bg-card p-2.5"
                    >
                      <span className="grid size-9 shrink-0 place-items-center rounded-full bg-primary font-bold text-primary-foreground">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold">{s.nombre}</p>
                        <p className="text-xs text-muted-foreground">
                          {s.legKm.toFixed(1)} km desde la anterior
                        </p>
                        {visited.has(s.id) ? (
                          <p className="flex items-center gap-1 text-xs font-semibold text-green-600">
                            <Check className="size-3.5" /> Visita registrada
                          </p>
                        ) : (
                          <Button
                            variant="link"
                            size="sm"
                            className="h-auto p-0 text-xs"
                            onClick={() => setVisiting(s)}
                          >
                            <ClipboardCheck /> Registrar visita
                          </Button>
                        )}
                        <Button
                          variant="link"
                          size="sm"
                          className="h-auto p-0 text-xs text-destructive"
                          aria-label={`Descartar ${s.nombre} para siempre`}
                          onClick={() => discard(s.id)}
                        >
                          <Trash2 />
                          {confirmDiscard === s.id
                            ? "Toca otra vez para descartar para siempre"
                            : "Descartar permanente"}
                        </Button>
                        {s.longLeg && (
                          <p className="flex items-center gap-1 text-xs font-semibold text-amber-600">
                            <Car className="size-3.5" /> Tramo largo: considera
                            ir en coche
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          className="size-10"
                          aria-label={`Subir ${s.nombre}`}
                          disabled={i === 0}
                          onClick={() => moveUp(i)}
                        >
                          <ArrowUp />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="size-10"
                          aria-label={`Bajar ${s.nombre}`}
                          disabled={i === stops.length - 1}
                          onClick={() => moveDown(i)}
                        >
                          <ArrowDown />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="size-10"
                          aria-label={`Quitar ${s.nombre} de la ruta`}
                          onClick={() => remove(s.id)}
                        >
                          <X />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ol>
                {discardError && (
                  <p role="alert" className="text-sm text-destructive">
                    {discardError}
                  </p>
                )}
                {saveError && (
                  <p className="text-sm text-amber-600">{saveError}</p>
                )}

                <div className="flex flex-col gap-2">
                  <div className="grid grid-cols-2 gap-2">
                    {(
                      [
                        ["walking", "Caminando"],
                        ["driving", "En coche"],
                      ] as const
                    ).map(([mode, label]) => (
                      <Button
                        key={mode}
                        variant={travelMode === mode ? "default" : "outline"}
                        onClick={() => setTravelMode(mode)}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                  {mapsSegments.map((seg) => (
                    <Button key={seg.index} asChild className="h-12">
                      <a
                        href={seg.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <MapPinned />
                        {seg.total === 1
                          ? "Abrir en Google Maps"
                          : `Abrir en Google Maps · Tramo ${seg.index} de ${seg.total}`}
                      </a>
                    </Button>
                  ))}
                </div>

                {state?.filter && (
                  <NextRoundButton
                    stops={stops}
                    filter={state.filter}
                    size={state.size}
                    seen={state.seen}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
      {visiting && (
        <VisitReport
          stop={visiting}
          stopName={visiting.nombre}
          preferredSlug={filterOfferSlug(state?.filter)}
          onClose={() => setVisiting(null)}
          onSaved={() => {
            setVisited((v) => new Set(v).add(visiting.id));
            setVisiting(null);
          }}
        />
      )}
    </MobilePage>
  );
};
